import React, { useEffect, useState } from "react";
import { FleetrifyModal } from "../../../Modal/modal";
import { FleetrifyButton } from "../../../Button/customButton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRejectInviteMutation, useGetHeadOrgInviteQuery } from "../../../../redux/services/companyApi";
import { rejectionReasons } from "../../../../utils/mock";
import FleetrifyRadioButton from "../../../Input/radio";
import { FleetrifyTextareaField } from "../../../Input/textArea";
import { AnimatePresence, motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import { FLEETRIFYROUTEPATH } from "../../../../routes/routesPath";

const RejectUserInviteModal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // get invite id from url
  const id = searchParams.get("id");

  // prefill state
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");

  // fetch invite details
  const { data: userData, isLoading: isUserLoading } = useGetHeadOrgInviteQuery(id ?? skipToken);

  useEffect(() => {
    if (userData) {
      setFirstname(userData.firstname);
      setLastname(userData.lastname);
      setEmail(userData.email);
    }
  }, [userData]);

  // reject reason state
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  const [rejectInvite, { isLoading }] = useRejectInviteMutation();

  const isSubmittable =
    selectedReason !== "" && (selectedReason !== "Others" || otherReason);

  const handleRejectUser = async () => {
    if (!isSubmittable || !id) return;

    try {
      await rejectInvite({
        id,
        reason: selectedReason === "Others" ? otherReason : selectedReason,
      }).unwrap();

      toast.success("Invitation rejected successfully!", {
        style: {
          background: "#e0f2fe",
          color: "#1E40AF",
          border: "1px solid #1E40AF",
        },
      });

      navigate(FLEETRIFYROUTEPATH.LOGIN);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to reject invite. Try again.", {
        style: {
          background: "#ffe5e5",
          color: "#ef4444",
          border: "1px solid #ff4d4f",
        },
      });
    }
  };

  return (
    <FleetrifyModal
      disableClose
      isOpen={true}
      className="max-w-xl h-[60vh] md:h-[47vh] lg:h-[70vh] flex flex-col hide-scrollbar"
      overlayClassName="z-[9999]"
      enableOutsideClick={false}
    >
      {/* header */}
      <div className="px-6 py-4 border-b border-gray-200 text-center flex-shrink-0">
        <h1 className="text-[#00032A] text-[20px] font-bold">Reject Invitation</h1>
        <p className="text-[#00032A] mt-1">
          {isUserLoading ? (
            <Skeleton width={120} height={20} />
          ) : (
            <span className="font-extrabold capitalize text-[18px]">
              {firstname} {lastname}
            </span>
          )}
        </p>
        <p className="text-[#00032A] mt-1 text-sm">
          Are you sure you want to reject this invitation? This action cannot be undone.
        </p>
      </div>

      {/* scrollable body */}
      <div className="px-6 py-4 flex-1 overflow-y-auto space-y-4">
        <h6 className="font-medium">Specify Reason</h6>
        {rejectionReasons.map((reason) => (
          <label key={reason} className="flex items-center space-x-2">
            <FleetrifyRadioButton
              name="rejectReason"
              value={reason}
              checked={selectedReason === reason}
              onValueChange={() => setSelectedReason(reason)}
            />
            <span className="text-sm font-medium">{reason}</span>
          </label>
        ))}

        <AnimatePresence>
          {selectedReason === "Others" && (
            <motion.div
              key="other-reason"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <FleetrifyTextareaField
                id="otherReason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Specify your other reason"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-center gap-4 flex-shrink-0">
        <FleetrifyButton
          onClick={handleRejectUser}
          className={`bg-[#EB5757] !py-[11px] px-[40px] text-white hover:bg-red-600 ${
            !isSubmittable ? "opacity-50 cursor-not-allowed" : ""
          }`}
          loading={isLoading}
          disabled={!isSubmittable || isUserLoading || !id}
        >
          Reject
        </FleetrifyButton>

        <button
          onClick={() => window.history.back()}
          className="px-6 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800"
        >
          Cancel
        </button>
      </div>
    </FleetrifyModal>
  );
};

export default RejectUserInviteModal;
